import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';

import { getApiErrorMessage } from '../utils/apiError';
import { useLayoutTokens } from '../hooks/useLayoutTokens';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

type HabitRecurrence = 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom';

const RECURRENCE_OPTIONS: { value: HabitRecurrence; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'custom', label: 'Custom' },
];

interface BulkEditItemResult {
  habit_id: number;
  status: string; // "ok" | "failed"
  error?: string | null;
}

interface BulkEditResponse {
  operation_id: number;
  total: number;
  succeeded: number;
  failed: number;
  results: BulkEditItemResult[];
}

interface HabitBulkEditDialogProps {
  open: boolean;
  habitIds: number[];
  // Minimal shape so the dialog stays decoupled from the full Habit type;
  // used only to render friendly names in the per-item failure list.
  habits: { id: number; title: string }[];
  onClose: () => void;
  // Called after a successful apply so the caller can re-fetch its habits.
  onApplied: () => void | Promise<void>;
}

// Bulk-edit dialog for the Habits page. Each supported field is opt-in: only
// the fields whose "change" checkbox is ticked are sent in `changes`, matching
// the PATCH /api/habits/bulk contract (which forbids unknown fields and
// requires at least one). Untouched fields on the selected habits are left
// exactly as they were.
const HabitBulkEditDialog: React.FC<HabitBulkEditDialogProps> = ({
  open,
  habitIds,
  habits,
  onClose,
  onApplied,
}) => {
  const { dialogContentPaddingSx } = useLayoutTokens();
  const [applyActive, setApplyActive] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [applyCheckIn, setApplyCheckIn] = useState(false);
  const [checkInEnabled, setCheckInEnabled] = useState(true);
  const [applyPriority, setApplyPriority] = useState(false);
  const [checkInPriority, setCheckInPriority] = useState(0);
  const [applyRecurrenceType, setApplyRecurrenceType] = useState(false);
  const [recurrenceType, setRecurrenceType] = useState<HabitRecurrence>('daily');
  const [applyInterval, setApplyInterval] = useState(false);
  const [recurrenceInterval, setRecurrenceInterval] = useState(1);
  const [applyTarget, setApplyTarget] = useState(false);
  const [targetCount, setTargetCount] = useState(1);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkEditResponse | null>(null);

  // Each fresh open starts from a blank slate so a previous run's field
  // choices and result summary never leak into the next edit.
  useEffect(() => {
    if (!open) return;
    setApplyActive(false);
    setIsActive(true);
    setApplyCheckIn(false);
    setCheckInEnabled(true);
    setApplyPriority(false);
    setCheckInPriority(0);
    setApplyRecurrenceType(false);
    setRecurrenceType('daily');
    setApplyInterval(false);
    setRecurrenceInterval(1);
    setApplyTarget(false);
    setTargetCount(1);
    setSubmitting(false);
    setSubmitError(null);
    setResult(null);
  }, [open]);

  const anyFieldEnabled =
    applyActive ||
    applyCheckIn ||
    applyPriority ||
    applyRecurrenceType ||
    applyInterval ||
    applyTarget;

  const titleFor = (habitId: number): string =>
    habits.find((habit) => habit.id === habitId)?.title ?? `Habit #${habitId}`;

  const handleApply = async () => {
    if (!anyFieldEnabled || habitIds.length === 0) return;

    const changes: Record<string, unknown> = {};
    if (applyActive) changes.is_active = isActive;
    if (applyCheckIn) changes.check_in_enabled = checkInEnabled;
    if (applyPriority) changes.check_in_priority = checkInPriority;
    if (applyRecurrenceType) changes.recurrence_type = recurrenceType;
    if (applyInterval) changes.recurrence_interval = recurrenceInterval;
    if (applyTarget) changes.target_count = targetCount;

    try {
      setSubmitting(true);
      setSubmitError(null);
      const response = await axios.patch<BulkEditResponse>(
        `${API_BASE_URL}/api/habits/bulk`,
        { habit_ids: habitIds, changes },
      );
      setResult(response.data);
      await onApplied();
    } catch (err: any) {
      setSubmitError(getApiErrorMessage(err, 'Failed to apply bulk edit'));
    } finally {
      setSubmitting(false);
    }
  };

  const failedResults = result?.results.filter((item) => item.status !== 'ok') ?? [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {result ? 'Bulk Edit Results' : `Edit ${habitIds.length} Selected Habits`}
      </DialogTitle>
      <DialogContent sx={dialogContentPaddingSx}>
        {result ? (
          <Box sx={{ pt: 1 }}>
            <Alert severity={result.failed > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
              {result.succeeded} of {result.total} habit
              {result.total === 1 ? '' : 's'} updated
              {result.failed > 0 ? ` — ${result.failed} failed` : ''}.
            </Alert>
            {failedResults.length > 0 && (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  These habits were not updated:
                </Typography>
                <List dense disablePadding>
                  {failedResults.map((item) => (
                    <ListItem key={item.habit_id} disableGutters sx={{ alignItems: 'flex-start' }}>
                      <ListItemText
                        primary={titleFor(item.habit_id)}
                        secondary={item.error || 'Unknown error'}
                        primaryTypographyProps={{ fontWeight: 600 }}
                        secondaryTypographyProps={{ color: 'error' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </Box>
        ) : (
          <Box sx={{ pt: 1 }}>
            <DialogContentText sx={{ mb: 2 }}>
              Choose which fields to change. Only the checked fields are applied — every
              other setting on the selected habits is left as-is.
            </DialogContentText>

            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
                {submitError}
              </Alert>
            )}

            {/* Active status */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <FormControlLabel
                sx={{ minWidth: 220, mr: 0 }}
                control={
                  <Checkbox
                    checked={applyActive}
                    onChange={(e) => setApplyActive(e.target.checked)}
                  />
                }
                label="Active status"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    disabled={!applyActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    inputProps={{ 'aria-label': 'Set active' }}
                  />
                }
                label={isActive ? 'Active' : 'Paused'}
              />
            </Box>

            {/* Check-in enabled */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <FormControlLabel
                sx={{ minWidth: 220, mr: 0 }}
                control={
                  <Checkbox
                    checked={applyCheckIn}
                    onChange={(e) => setApplyCheckIn(e.target.checked)}
                  />
                }
                label="Check-in enabled"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={checkInEnabled}
                    disabled={!applyCheckIn}
                    onChange={(e) => setCheckInEnabled(e.target.checked)}
                    inputProps={{ 'aria-label': 'Set check-in enabled' }}
                  />
                }
                label={checkInEnabled ? 'In queue' : 'Excluded'}
              />
            </Box>

            {/* Check-in priority */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <FormControlLabel
                sx={{ minWidth: 220, mr: 0 }}
                control={
                  <Checkbox
                    checked={applyPriority}
                    onChange={(e) => setApplyPriority(e.target.checked)}
                  />
                }
                label="Check-in priority"
              />
              <TextField
                type="number"
                size="small"
                label="Priority"
                disabled={!applyPriority}
                inputProps={{ min: 0, 'aria-label': 'Check-in priority value' }}
                value={checkInPriority}
                onChange={(e) => setCheckInPriority(Number(e.target.value))}
                sx={{ width: 140 }}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Recurrence type */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <FormControlLabel
                sx={{ minWidth: 220, mr: 0 }}
                control={
                  <Checkbox
                    checked={applyRecurrenceType}
                    onChange={(e) => setApplyRecurrenceType(e.target.checked)}
                  />
                }
                label="Recurrence type"
              />
              <FormControl size="small" sx={{ width: 180 }} disabled={!applyRecurrenceType}>
                <InputLabel id="bulk-recurrence-type-label">Type</InputLabel>
                <Select
                  labelId="bulk-recurrence-type-label"
                  label="Type"
                  value={recurrenceType}
                  onChange={(e) => setRecurrenceType(e.target.value as HabitRecurrence)}
                >
                  {RECURRENCE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Recurrence interval */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <FormControlLabel
                sx={{ minWidth: 220, mr: 0 }}
                control={
                  <Checkbox
                    checked={applyInterval}
                    onChange={(e) => setApplyInterval(e.target.checked)}
                  />
                }
                label="Recurrence interval"
              />
              <TextField
                type="number"
                size="small"
                label="Interval"
                disabled={!applyInterval}
                inputProps={{ min: 1, 'aria-label': 'Recurrence interval value' }}
                value={recurrenceInterval}
                onChange={(e) => setRecurrenceInterval(Number(e.target.value))}
                sx={{ width: 140 }}
              />
            </Box>

            {/* Target count */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <FormControlLabel
                sx={{ minWidth: 220, mr: 0 }}
                control={
                  <Checkbox
                    checked={applyTarget}
                    onChange={(e) => setApplyTarget(e.target.checked)}
                  />
                }
                label="Target count"
              />
              <TextField
                type="number"
                size="small"
                label="Target"
                disabled={!applyTarget}
                inputProps={{ min: 1, 'aria-label': 'Target count value' }}
                value={targetCount}
                onChange={(e) => setTargetCount(Number(e.target.value))}
                sx={{ width: 140 }}
              />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {result ? (
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              variant="contained"
              disabled={!anyFieldEnabled || submitting || habitIds.length === 0}
            >
              {submitting ? (
                <CircularProgress size={20} />
              ) : (
                `Apply to ${habitIds.length} habit${habitIds.length === 1 ? '' : 's'}`
              )}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default HabitBulkEditDialog;
